export interface WorkflowStateSummary {
  name: string;
  shortLabel: string;
  label: string;
}

export interface WorkflowTransitionSummary {
  label: string;
  targetState: WorkflowStateSummary;
  required?: boolean;
  expects?: {
    mandatories: string[];
    optionals: string[];
  };
  isCommentMandatory?: boolean;
  isCommentForCustomerAvailable?: boolean;
}

export interface WorkflowModuleSummary {
  id: string;
  definitionId: string;
  wave: number;
  parameters: Record<string, unknown>;
}

export interface WorkflowActionSummary {
  fileId: string;
  module: WorkflowModuleSummary;
  stateDate: string;
  priority: number;
  currentState: WorkflowStateSummary;
  transitions: WorkflowTransitionSummary[];
}

const SAV_MODULE: WorkflowModuleSummary = {
  id: 'module-sav-demo',
  definitionId: 'workflow-sav-demo',
  wave: 1,
  parameters: {},
};

const PAYMENT_MODULE: WorkflowModuleSummary = {
  id: 'module-payment-demo',
  definitionId: 'workflow-payment-demo',
  wave: 1,
  parameters: {},
};

const buildAction = ({
  fileId,
  stateDate,
  currentState,
  transitions,
  priority = 1,
  module = SAV_MODULE,
}: {
  fileId: string;
  stateDate: string;
  currentState: WorkflowStateSummary;
  transitions: WorkflowTransitionSummary[];
  priority?: number;
  module?: WorkflowModuleSummary;
}): WorkflowActionSummary => ({
  fileId,
  module,
  stateDate,
  priority,
  currentState,
  transitions,
});

const MOCK_WORKFLOW_ACTIONS: WorkflowActionSummary[] = [
  buildAction({
    fileId: 'a1000001-0000-4000-8000-000000000001',
    stateDate: '2024-02-20T14:30:00.000Z',
    currentState: {
      name: 'FILE_CLOSED',
      shortLabel: 'Dossier clôturé',
      label: 'Dossier cloturé',
    },
    transitions: [],
  }),
  buildAction({
    fileId: 'a1000002-0000-4000-8000-000000000002',
    stateDate: '2024-02-22T09:15:00.000Z',
    currentState: {
      name: 'SECOND_REMINDER_SENT',
      shortLabel: 'Deuxième relance',
      label: "Deuxième relance envoyée à l'acteur du dossier",
    },
    transitions: [
      {
        label: 'Relancer le client',
        targetState: {
          name: 'THIRD_REMINDER_SENT',
          shortLabel: 'Troisième relance',
          label: 'Troisième relance envoyée au client',
        },
        isCommentForCustomerAvailable: true,
      },
      {
        label: 'Clôturer le dossier',
        targetState: {
          name: 'FILE_CLOSED',
          shortLabel: 'Dossier clôturé',
          label: 'Dossier cloturé',
        },
        isCommentMandatory: true,
      },
      {
        label: 'Passer en attente pièce',
        targetState: {
          name: 'WAITING_SPARE_PART',
          shortLabel: 'Attente pièce',
          label: 'En attente de pièce détachée',
        },
      },
    ],
  }),
  buildAction({
    fileId: 'a1000003-0000-4000-8000-000000000003',
    stateDate: '2024-02-25T16:45:00.000Z',
    currentState: {
      name: 'CARRIER_PICKED_UP',
      shortLabel: 'Pris en charge transporteur',
      label: 'Colis pris en charge par le transporteur',
    },
    transitions: [
      {
        label: 'Confirmer réception atelier',
        targetState: {
          name: 'RECEIVED_AT_WORKSHOP',
          shortLabel: 'Réception atelier',
          label: 'Colis reçu en atelier',
        },
      },
      {
        label: 'Signaler un problème transport',
        targetState: {
          name: 'TRANSPORT_ISSUE',
          shortLabel: 'Problème transport',
          label: 'Problème signalé avec le transport',
        },
        isCommentMandatory: true,
        isCommentForCustomerAvailable: true,
      },
    ],
  }),
  // Deux modules en parallèle (comme Control New UI)
  buildAction({
    fileId: 'a1000004-0000-4000-8000-000000000004',
    stateDate: '2024-02-26T11:00:00.000Z',
    priority: 1,
    currentState: {
      name: 'WAITING_RMA_SHIPMENT',
      shortLabel: 'Attente expédition RMA',
      label: "En attente d'expédition [RMA automatique]",
    },
    transitions: [
      {
        label: 'Valider expédition RMA',
        targetState: {
          name: 'RMA_SHIPPED',
          shortLabel: 'RMA expédié',
          label: 'RMA expédié au client',
        },
      },
      {
        label: 'Annuler le RMA',
        targetState: {
          name: 'RMA_CANCELLED',
          shortLabel: 'RMA annulé',
          label: 'Demande RMA annulée',
        },
        isCommentMandatory: true,
      },
      {
        label: 'Demander une pièce supplémentaire',
        targetState: {
          name: 'WAITING_SPARE_PART',
          shortLabel: 'Attente pièce',
          label: 'En attente de pièce détachée',
        },
        expects: {
          mandatories: ['UNUSED_SPARE_PARTS'],
          optionals: [],
        },
      },
    ],
  }),
  buildAction({
    fileId: 'a1000004-0000-4000-8000-000000000004',
    stateDate: '2024-02-26T11:05:00.000Z',
    priority: 2,
    module: PAYMENT_MODULE,
    currentState: {
      name: 'WAITING_PAYMENT_COLLECTION',
      shortLabel: 'Encaissement en attente',
      label: 'En attente d’encaissement client',
    },
    transitions: [
      {
        label: 'Confirmer le paiement reçu',
        targetState: {
          name: 'PAYMENT_RECEIVED',
          shortLabel: 'Paiement reçu',
          label: 'Paiement client reçu',
        },
      },
      {
        label: 'Relancer le paiement',
        targetState: {
          name: 'PAYMENT_REMINDER_SENT',
          shortLabel: 'Relance paiement',
          label: 'Relance de paiement envoyée',
        },
        isCommentForCustomerAvailable: true,
      },
      {
        label: 'Annuler l’encaissement',
        targetState: {
          name: 'PAYMENT_CANCELLED',
          shortLabel: 'Encaissement annulé',
          label: 'Encaissement annulé',
        },
        isCommentMandatory: true,
      },
    ],
  }),
  buildAction({
    fileId: 'a1000005-0000-4000-8000-000000000005',
    stateDate: '2024-02-27T08:30:00.000Z',
    currentState: {
      name: 'RMA_PENDING_SUPPLIER',
      shortLabel: 'RMA en attente fournisseur',
      label:
        'Demande RMA en attente de confirmation par le fournisseur [RMA manuelle]',
    },
    transitions: [
      {
        label: 'Confirmer RMA fournisseur',
        targetState: {
          name: 'RMA_CONFIRMED',
          shortLabel: 'RMA confirmé',
          label: 'RMA confirmé par le fournisseur',
        },
      },
      {
        label: 'Refuser la garantie',
        targetState: {
          name: 'WARRANTY_REJECTED',
          shortLabel: 'Garantie refusée',
          label: 'Garantie refusée par le fournisseur',
        },
        isCommentMandatory: true,
        expects: {
          mandatories: ['REJECT_WARRANTY'],
          optionals: [],
        },
      },
      {
        label: 'Proposer une solution commerciale',
        targetState: {
          name: 'COMMERCIAL_SOLUTION_PROPOSED',
          shortLabel: 'Solution commerciale',
          label: 'Solution commerciale proposée au client',
        },
        expects: {
          mandatories: ['MAKE_MARKETING_OFFER'],
          optionals: [],
        },
      },
    ],
  }),
];

export const getWorkflowActionsByFileId = (
  fileId: string | undefined,
): WorkflowActionSummary[] => {
  if (!fileId) {
    return [];
  }

  return MOCK_WORKFLOW_ACTIONS.filter((action) => action.fileId === fileId).sort(
    (left, right) => left.priority - right.priority,
  );
};
